/**
 * UNDERLAY (owner B2): the region pack (z ≤ 11) drawn again beneath the detailed basemap.
 *
 * The basemap pack holds z12–14 only inside METRO_BBOX and the corridor buffers (M1). Elsewhere a z12+
 * tile comes back empty (B1), so without help Lonavala or Navi Mumbai at z13 would show bare background.
 * The underlay source reads the same in-memory archive through the plain tile template with maxzoom 11,
 * so MapLibre overzooms the region tiles there. Its layers:
 *   - start at z12 (below that the basemap covers the whole region, so the underlay never loads);
 *   - sit directly above the background, so wherever detailed tiles exist their opaque earth hides it;
 *   - carry the land (earth, landuse, water, rivers) and the major roads with their casings, and
 *     the locality names (placed just beneath the basemap's own, so collisions keep one of each).
 * Colours follow the mood exactly like the basemap (same flavor, same paint diffs on a mood change).
 */
import type { LayerSpecification, VectorSourceSpecification } from "maplibre-gl";
import type { Layout, Mood } from "@/contracts/store";
import { underlaySource } from "@/map/memorySource";
import { basemapLayers } from "@/map/style/flavors";

export const UNDERLAY_SOURCE_ID = "ld-underlay";
export const UNDERLAY_PREFIX = "ld-under-";
/** The underlay appears where the detailed pack may be missing (z ≥ 12). */
export const UNDERLAY_MINZOOM = 12;

/** Protomaps ids drawn from the region pack (fills and lines, bottom to top). */
const UNDER_BASE = new Set([
  "earth",
  "landuse_park",
  "landuse_urban_green",
  "landuse_hospital",
  "landuse_industrial",
  "landuse_school",
  "landuse_beach",
  "landuse_zoo",
  "landuse_aerodrome",
  "landuse_runway",
  "water",
  "water_river",
  "landuse_pedestrian",
  "roads_major_casing_late",
  "roads_highway_casing_late",
  "roads_major",
  "roads_highway",
]);
const UNDER_LABELS = new Set(["places_locality"]);

export function underlaySourceSpec(): VectorSourceSpecification {
  return underlaySource();
}

export interface UnderlayLayers {
  /** Land, water and roads: inserted right after the background. */
  base: LayerSpecification[];
  /** Locality names: inserted just beneath the basemap's label layers. */
  labels: LayerSpecification[];
}

export function underlayLayers(mood: Mood, o: { layout?: Layout } = {}): UnderlayLayers {
  const all = basemapLayers(mood, UNDERLAY_SOURCE_ID, { layout: o.layout, idPrefix: UNDERLAY_PREFIX });
  const base: LayerSpecification[] = [];
  const labels: LayerSpecification[] = [];
  for (const l of all) {
    const pid = l.id.slice(UNDERLAY_PREFIX.length);
    const floor = { ...l, minzoom: Math.max(l.minzoom ?? 0, UNDERLAY_MINZOOM) } as LayerSpecification;
    // A layer with a maxzoom below the floor (the *_casing_early roads) never shows here.
    if ("maxzoom" in l && typeof l.maxzoom === "number" && l.maxzoom <= UNDERLAY_MINZOOM) continue;
    if (UNDER_BASE.has(pid)) base.push(floor);
    else if (UNDER_LABELS.has(pid)) labels.push(floor);
  }
  return { base, labels };
}

export const isUnderlayLayer = (id: string): boolean => id.startsWith(UNDERLAY_PREFIX);
